import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { loginSchema, type LoginInput, APP_VERSION } from '@bstock/shared';
import { useAuth } from '@/context/AuthContext';
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import bstockLogo from '@/assets/bstock-logo.svg';

export default function LoginPage() {
  const { login } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setLoginError(null);
    const result = await login(data);
    if (!result.success && result.error) {
      setLoginError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={bstockLogo} alt="BStock" className="h-12" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">Connexion</CardTitle>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {loginError && (
              <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                {loginError}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              placeholder="email@exemple.com"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Mot de passe"
              type="password"
              placeholder="Votre mot de passe"
              error={errors.password?.message}
              {...register('password')}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsForgotPasswordOpen(true)}
                className="text-sm text-primary-600 hover:underline"
              >
                Mot de passe oublie ?
              </button>
            </div>

            <Button type="submit" className="w-full" isLoading={isSubmitting}>
              Se connecter
            </Button>

            <p className="text-center text-xs text-neutral-400">v{APP_VERSION}</p>
          </form>
        </CardContent>
      </Card>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
      />
    </div>
  );
}
